<template>
    <div>
        <v-col cols="12">
            <h2>{{ id ? t('crons::page.title.edit_cron') : t('crons::page.title.new_cron') }}</h2>
        </v-col>
        <v-col cols="12">
            <div class="form-wrapper">
                <v-form ref="form" v-model="isFormValid">
                    <v-container>
                        <v-row>
                            <v-col cols="12" lg="4">
                                <v-text-field
                                    v-model="cron.name"
                                    dense
                                    background-color="#fff"
                                    outlined
                                    rounded
                                    :label="`${$root.defaultMandatoryAsteriks} ${t('crons::edit.placeholder.name')}`"
                                    :rules="[(v) => !!v || t('default::validation.field.required')]"
                                >
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" lg="4">
                                <v-text-field
                                    v-model="cron.command"
                                    dense
                                    background-color="#fff"
                                    outlined
                                    rounded
                                    :label="`${$root.defaultMandatoryAsteriks} ${t('crons::edit.placeholder.command')}`"
                                    :rules="[(v) => !!v || t('default::validation.field.required')]"
                                    :hint="commandLineHint"
                                    persistent-hint
                                >
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" lg="4">
                                <v-switch
                                    v-model="cron.is_active"
                                    class="mt-0"
                                    :label="
                                        cron.is_active ? t('crons::edit.label.active') : t('crons::edit.label.inactive')
                                    "
                                    :true-value="true"
                                    :false-value="false"
                                >
                                </v-switch>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" class="pb-0">
                                <v-textarea
                                    v-model="cron.description"
                                    outlined
                                    rounded
                                    dense
                                    rows="1"
                                    background-color="#fff"
                                    :label="`${$root.defaultMandatoryAsteriks} ${t(
                                        'crons::edit.placeholder.description'
                                    )}`"
                                    :rules="[(v) => !!v || t('default::validation.field.required')]"
                                ></v-textarea>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <v-card style="border-radius: 16px">
                                    <v-toolbar flat dense color="primary" dark>
                                        <v-toolbar-title>{{ t('crons::schedule.options') }}</v-toolbar-title>
                                    </v-toolbar>
                                    <v-card-text>
                                        <v-row>
                                            <v-col cols="12" lg="4" class="fix-radio">
                                                <radio-group
                                                    v-model="minutes"
                                                    :title="t('crons::radio_group.minutes.title')"
                                                    :selected="minutes"
                                                    :radio-data="minutesData"
                                                    :select-placeholder="minutesSelectPlaceholder"
                                                    :custom-values="minutesCustom"
                                                ></radio-group>
                                            </v-col>
                                            <v-col cols="12" lg="4">
                                                <radio-group
                                                    v-model="hours"
                                                    :title="t('crons::radio_group.hours.title')"
                                                    :selected="hours"
                                                    :radio-data="hoursData"
                                                    :select-placeholder="hoursSelectPlaceholder"
                                                    :custom-values="hoursCustom"
                                                ></radio-group>
                                            </v-col>
                                            <v-col cols="12" lg="4">
                                                <radio-group
                                                    v-model="days"
                                                    :title="t('crons::radio_group.days.title')"
                                                    :selected="days"
                                                    :radio-data="daysData"
                                                    :select-placeholder="daysSelectPlaceholder"
                                                    :custom-values="daysCustom"
                                                ></radio-group>
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12" lg="4">
                                                <radio-group
                                                    v-model="months"
                                                    :title="t('crons::radio_group.months.title')"
                                                    :selected="months"
                                                    :radio-data="monthsData"
                                                    :select-placeholder="monthsSelectPlaceholder"
                                                    :custom-values="monthsCustom"
                                                ></radio-group>
                                            </v-col>
                                            <v-col cols="12" lg="4">
                                                <radio-group
                                                    v-model="weekday"
                                                    :title="t('crons::radio_group.weekday.title')"
                                                    :selected="weekday"
                                                    :radio-data="weekdayData"
                                                    :select-placeholder="weekdaySelectPlaceholder"
                                                    :custom-values="weekdayCustom"
                                                ></radio-group>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-container>
                    <b-form-actions
                        :id="id"
                        :back-route="$root.route('crons.index')"
                        @discard="reset"
                        @save="save"
                    ></b-form-actions>
                </v-form>
            </div>
        </v-col>
    </div>
</template>

<script>
let formSubmitMixin = require(process.env.MIX_APP_ROOT +
    '/bgo/bgo-components/resources/js/mixins/FormSubmit.js').default;

export default {
    name: 'CronFormComponent',
    mixins: [formSubmitMixin],
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: true,
            saveLoading: false,
            isFormValid: false,
            commandLineHint: t('crons::edit.hint.command'),
            minutesSelectPlaceholder: t('crons::minutes.placeholder.custom'),
            hoursSelectPlaceholder: t('crons::hours.placeholder.custom'),
            daysSelectPlaceholder: t('crons::days.placeholder.custom'),
            monthsSelectPlaceholder: t('crons::months.placeholder.custom'),
            weekdaySelectPlaceholder: t('crons::weekday.placeholder.custom'),
            minutes: '*',
            hours: '*',
            days: '*',
            months: '*',
            weekday: '*',
            cron: {
                name: null,
                is_active: true,
                command: null,
                description: null,
                schedule: null,
                last_run_at: null,
                next_run_at: null,
                last_started_at: null,
                last_finished_at: null,
            },
            minutesData: [
                { label: t('crons::schedule.option.minutes.every'), value: '*' },
                { label: t('crons::schedule.option.minutes.even'), value: '*/2' },
                { label: t('crons::schedule.option.minutes.odd'), value: '1-59/2' },
                { label: t('crons::schedule.option.minutes.every_5'), value: '*/5' },
                { label: t('crons::schedule.option.minutes.every_15'), value: '*/15' },
                { label: t('crons::schedule.option.minutes.every_30'), value: '*/30' },
                { label: t('crons::schedule.option.minutes.custom'), value: 'custom' },
            ],
            hoursData: [
                { label: t('crons::schedule.option.hours.every'), value: '*' },
                { label: t('crons::schedule.option.hours.even'), value: '*/2' },
                { label: t('crons::schedule.option.hours.odd'), value: '1-23/2' },
                { label: t('crons::schedule.option.hours.every_6'), value: '*/6' },
                { label: t('crons::schedule.option.hours.every_12'), value: '*/12' },
                { label: t('crons::schedule.option.hours.custom'), value: 'custom' },
            ],
            daysData: [
                { label: t('crons::schedule.option.days.every'), value: '*' },
                { label: t('crons::schedule.option.days.even'), value: '*/2' },
                { label: t('crons::schedule.option.days.odd'), value: '1-23/2' },
                { label: t('crons::schedule.option.days.every_5'), value: '*/4' },
                { label: t('crons::schedule.option.days.every_10'), value: '*/6' },
                { label: t('crons::schedule.option.days.every_half_month'), value: '1,15' },
                { label: t('crons::schedule.option.days.custom'), value: 'custom' },
            ],
            monthsData: [
                { label: t('crons::schedule.option.months.every'), value: '*' },
                { label: t('crons::schedule.option.months.even'), value: '*/2' },
                { label: t('crons::schedule.option.months.odd'), value: '1-23/2' },
                { label: t('crons::schedule.option.months.every_4'), value: '*/4' },
                { label: t('crons::schedule.option.months.every_half_year'), value: '*/6' },
                { label: t('crons::schedule.option.months.custom'), value: 'custom' },
            ],
            weekdayData: [
                { label: t('crons::schedule.option.weekday.every'), value: '*' },
                { label: t('crons::schedule.option.weekday.mon_fri'), value: '1-5' },
                { label: t('crons::schedule.option.weekday.weekend'), value: '0,6' },
                { label: t('crons::schedule.option.weekday.custom'), value: 'custom' },
            ],
            minutesCustom: _.map(_.range(0, 60), function (el) {
                return { label: el, value: el.toString() };
            }),
            hoursCustom: _.map(_.range(0, 24), function (el) {
                return { label: el, value: el.toString() };
            }),
            daysCustom: _.map(_.range(0, 31), function (el) {
                return { label: el, value: el.toString() };
            }),
            monthsCustom: [
                { label: 0, value: '0' },
                { label: t('crons::months.january'), value: '1' },
                { label: t('crons::months.february'), value: '2' },
                { label: t('crons::months.march'), value: '3' },
                { label: t('crons::months.april'), value: '4' },
                { label: t('crons::months.may'), value: '5' },
                { label: t('crons::months.june'), value: '6' },
                { label: t('crons::months.july'), value: '7' },
                { label: t('crons::months.august'), value: '8' },
                { label: t('crons::months.september'), value: '9' },
                { label: t('crons::months.october'), value: '10' },
                { label: t('crons::months.november'), value: '11' },
                { label: t('crons::months.december'), value: '12' },
            ],
            weekdayCustom: [
                { label: 0, value: '0' },
                { label: t('crons::days.mondays'), value: '1' },
                { label: t('crons::days.tuesdays'), value: '2' },
                { label: t('crons::days.wednesdays'), value: '3' },
                { label: t('crons::days.thursdays'), value: '4' },
                { label: t('crons::days.fridays'), value: '5' },
                { label: t('crons::days.saturdays'), value: '6' },
                { label: t('crons::days.sundays'), value: '7' },
            ],
        };
    },
    created() {
        this.loadRoute();
    },
    methods: {
        loadRoute() {
            if (_.isEmpty(this.id)) {
                this.loading = false;
                return;
            }
            axios
                .get(route('api.v1.crons.show', this.id))
                .then((response) => {
                    this.cron = response.data.data;

                    var schedule = response.data.data.schedule.split(' ');
                    this.minutes = schedule[0];
                    this.hours = schedule[1];
                    this.days = schedule[2];
                    this.months = schedule[3];
                    this.weekday = schedule[4];
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        reset() {
            this.route = _.clone(this.routeOriginal);
        },
        save() {
            this.cron.schedule = `${this.minutes} ${this.hours} ${this.days} ${this.months} ${this.weekday}`;

            if (this.id) {
                this.submit(
                    route('api.v1.crons.update', {
                        cron: this.id,
                    }),
                    this.cron,
                    'put',
                    () => {
                        this.$root.notify('info', t('default::notification.saved'));
                        this.$root.goTo(route('crons.index'));
                    }
                );
            } else {
                this.submit(route('api.v1.crons.store'), this.cron, 'post', () => {
                    this.$root.notify('info', t('default::notification.saved'));
                    this.$root.goTo(route('crons.index'));
                });
            }
        },
    },
};
</script>
